import React from 'react';
import MyAccountPageShell from './MyAccountPageShell';

interface LoadingBarProps {
    height?: string;
    width?: string;
    style?: React.CSSProperties;
}

interface PageShellLoadingProps {
    className?: string;
    showMeta?: boolean;
    showAction?: boolean;
    titleWidth?: string;
    subtitleWidth?: string;
    metaWidth?: string;
    actionWidth?: string;
    children: React.ReactNode;
}

interface ToolbarSkeletonProps {
    columns?: Array<string>;
    className?: string;
}

interface TableSkeletonProps {
    columns?: number;
    rows?: number;
}

interface SummaryCardsSkeletonProps {
    count?: number;
    minHeight?: string;
}

const LoadingBar: React.FC<LoadingBarProps> = ({
    height = '16px',
    width = '100%',
    style,
}) => (
    <span
        className="skeleton d-block"
        style={{
            height,
            width,
            marginBottom: 0,
            ...style,
        }}
    />
);

export const PageShellLoading: React.FC<PageShellLoadingProps> = ({
    className = '',
    showMeta = false,
    showAction = false,
    titleWidth = '30%',
    subtitleWidth = '56%',
    metaWidth = '84px',
    actionWidth = '140px',
    children,
}) => (
    <MyAccountPageShell
        className={className}
        title={<LoadingBar height="28px" width={titleWidth} />}
        subtitle={<LoadingBar height="16px" width={subtitleWidth} />}
        meta={showMeta ? <LoadingBar height="14px" width={metaWidth} style={{ marginTop: '0.25rem' }} /> : undefined}
        actions={showAction ? <LoadingBar height="38px" width={actionWidth} style={{ borderRadius: '8px' }} /> : undefined}
    >
        {children}
    </MyAccountPageShell>
);

export const ToolbarSkeleton: React.FC<ToolbarSkeletonProps> = ({
    columns = ['col-md-6', 'col-md-6'],
    className = 'mb-3',
}) => (
    <div className={`row ${className}`.trim()}>
        {columns.map((columnClassName, index) => (
            <div key={`toolbar-skeleton-${index}`} className={columnClassName}>
                <LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} />
            </div>
        ))}
    </div>
);

const getTableLineWidth = (columnIndex: number, rowIndex: number): string => {
    const baseWidths = ['72%', '58%', '42%', '60%', '46%', '38%', '54%', '36%'];
    const altWidths = ['64%', '48%', '34%', '52%', '38%', '30%', '46%', '28%'];

    return rowIndex % 2 === 0
        ? (baseWidths[columnIndex] || '52%')
        : (altWidths[columnIndex] || '44%');
};

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
    columns = 5,
    rows = 6,
}) => (
    <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
            <thead>
                <tr>
                    {Array.from({ length: columns }).map((_, columnIndex) => (
                        <th key={`table-skeleton-header-${columnIndex}`}>
                            <LoadingBar
                                height="12px"
                                width={columnIndex === 0 ? '58%' : '42%'}
                                style={{ marginBottom: '0.25rem' }}
                            />
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: rows }).map((_, rowIndex) => (
                    <tr key={`table-skeleton-row-${rowIndex}`}>
                        {Array.from({ length: columns }).map((__, columnIndex) => (
                            <td key={`table-skeleton-cell-${rowIndex}-${columnIndex}`}>
                                <LoadingBar
                                    height="18px"
                                    width={getTableLineWidth(columnIndex, rowIndex)}
                                />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export const SummaryCardsSkeleton: React.FC<SummaryCardsSkeletonProps> = ({
    count = 4,
    minHeight = '120px',
}) => (
    <div className="row g-3">
        {Array.from({ length: count }).map((_, index) => (
            <div key={`summary-card-skeleton-${index}`} className="col-12 col-md-6 col-xl-3">
                <div className="border rounded p-3 h-100" style={{ minHeight }}>
                    <LoadingBar height="14px" width="52%" style={{ marginBottom: '12px' }} />
                    <LoadingBar height="26px" width="72%" />
                </div>
            </div>
        ))}
    </div>
);

interface ChartSkeletonProps {
    showHeader?: boolean;
}

export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ showHeader = true }) => (
    <div>
        {showHeader && (
            <>
                <LoadingBar height="24px" width="32%" style={{ marginBottom: '6px' }} />
                <LoadingBar height="14px" width="24%" style={{ marginBottom: '24px' }} />
            </>
        )}
        <div style={{ position: 'relative', height: '300px', paddingLeft: '56px' }}>
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: '40px',
                    width: '46px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <LoadingBar height="12px" width="70%" />
                <LoadingBar height="12px" width="55%" />
                <LoadingBar height="12px" width="30%" />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '260px', borderBottom: '1px solid var(--weka-card-border)' }}>
                {Array.from({ length: 12 }).map((_, index) => (
                    <span
                        key={`chart-bar-skeleton-${index}`}
                        className="skeleton d-block"
                        style={{
                            flex: 1,
                            height: `${48 + ((index % 5) * 36)}px`,
                            borderRadius: '4px 4px 0 0',
                            marginBottom: 0,
                        }}
                    />
                ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                {Array.from({ length: 12 }).map((_, index) => (
                    <LoadingBar
                        key={`chart-label-skeleton-${index}`}
                        height="12px"
                        width="100%"
                        style={{ flex: 1 }}
                    />
                ))}
            </div>
        </div>
    </div>
);

export { LoadingBar };
